import { Injectable, inject } from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import { ApolloError } from '@apollo/client/errors';
import { TranslationService } from './translation/translation.service';

/**
 * Error messages by keys
 */
export const ERROR_MESSAGES: any = {
  required: 't.cant-blank',
  required_true: 't.must-accepted',
  pattern: 't.invalid-format',
  password_length: 't.length-symbols',
  must_match: 't.passwds-not-match',
  eighteen_years: 't.18-years-err',
  min: 't.amount-below-limit',
  max: 't.amount-above-limit',
  maxlength: 't.error-max-length',
  minlength: 't.error-min-length',
  invalid_year: 't.invalid-year',
  invalid_month: 't.invalid-month',
  invalid_month_format: 't.invalid-month-format',
};

const ERROR_DYNAMIC_MESSAGES: any = {
  password_pattern: 't.paswd-requirments',
  password_en_pattern: 't.pass-not-en'
};

@Injectable({
  providedIn: 'root'
})
export class FormsErrorHandlerService {
  private _translate = inject(TranslationService);


  /**
   * Apply form errors with messages
   * And return object with errors for each key
   *
   * @param form
   * @param ssResponse
   * @param touch
   */
  public applyFormErrors(form: FormGroup, ssResponse: any = null, touch = false): any {

    const formErrors: any = {};

    ssResponse = ssResponse || {errors: {}};

    Object.keys(form.controls).forEach(controlKey => {
      const control: any = form.get(controlKey);

      let errors = {
        ...control.errors || {},
        ...(ssResponse.errors && ssResponse.errors[controlKey] || {})
      };

      const errorKeys = Object.keys(errors);

      if (errorKeys.length) {
        errorKeys.forEach(errorKey => {
          errors[errorKey] = this.getErrorMessageByKey(errorKey) || errors[errorKey];
        });
      } else {
        errors = null;
      }

      control.setErrors(errors);

      formErrors[controlKey] = this.errors(control);
      if (touch) {
        control.markAsTouched();
      }
    });

    /**
     * Uses for get errors which key not match control key
     */
    Object.keys(ssResponse).forEach(errorKey => {
      Object.keys(ssResponse[errorKey]).forEach(key => {
        if (!formErrors[key]) {
          formErrors[key] = Object.keys(ssResponse[errorKey][key]).map(error => ssResponse[errorKey][key][error]);
        }
      });
    });

    return formErrors;
  }

  /**
   * Returns array of error messages if exists for provided control
   *
   * @param control
   */
  public errors(control: AbstractControl | any): Array<string> {
    return Object.keys(control.errors || {}).map(key => {
      if (key === 't.min' || key === 't.max') {
        return `${key} ${control.errors[key][key]}`;
      } else {
        return this.getErrorMessageByKey(key, control.errors[key]) || control.errors[key];
      }
    });
  }

  /**
   * Returns error message for provided key
   *
   * @param key
   */
  public getErrorMessageByKey(key: string, msg?: any): string {
    if (ERROR_DYNAMIC_MESSAGES[key]) {
      return this._translate.translate(ERROR_DYNAMIC_MESSAGES[key], {data: msg});
    }
    return ERROR_MESSAGES[key] || '';
  }

  /**
   * Convert SS error response to array of errors
   *
   * @param error
   */
  public ssBackendErrorsToArray(error: any): Array<string> {
    const errorList: any[] = [];

    if (error) {
      Object.keys(error).forEach(key => {
        const type = typeof error[key];

        if (type === 'object') {
          errorList.push(...this.ssBackendErrorsToArray(error[key]));
        } else if (type === 'string') {
          errorList.push(error[key]);
        }
      });
    }

    return errorList;
  }

  /**
   * Apply form errors with messages
   * @param form
   * @param response
   * @param touch
   */
  public applyApolloErrors(form: FormGroup, error: ApolloError = null, touch = false): any {
    const formErrors: any = {};
    const platformErrors: any = this._parsePlatformErrors(error) || {errors: {}};

    Object.keys(form.controls).forEach(controlKey => {
      const control: any = form.get(controlKey);
      let errors = {
        ...control.errors || {},
        ...(platformErrors?.errors[controlKey] || {})
      };

      const errorKeys = Object.keys(errors);
      if (errorKeys.length) {
        errorKeys.forEach(errorKey => {
          errors[errorKey] = this.getErrorMessageByKey(errorKey) || errors[errorKey];
        });
      } else {
        errors = null;
      }

      control.setErrors(errors);
      formErrors[controlKey] = this.errors(control);
      if (touch) {
        control.markAsTouched();
      }
    });
    return formErrors;
  }

  private _parsePlatformErrors(error: ApolloError) {
    try {
      const errorPath = error?.message?.split(':')[0];
      const errorValue = error?.message?.split(':')[1];
      const errorKeyList = errorPath?.split('.');
      return {errors: {
        [errorKeyList[errorKeyList?.length - 1]]: [errorValue]
      }};
    } catch {
      return {errors: {}};
    }
  }

}
